import { useCallback } from 'react';
import { useTableContext } from 'ksnm-common-ui/lib/Table2/contexts';
import type { ColumnInterface } from 'ksnm-common-ui/lib/Table2/Table.interface';

import { TABLE_CONFIG } from '@stores/ksnmTableStore/ksnmTableStore';

type IUseToggleFullSize = {
  tableName: string;
  columns: ColumnInterface[];
  fullSize: boolean;
  toggle: () => void;
  update?: () => void;
  withCheckbox?: boolean;
};

const calcNewWidth = (colWidth: { [key: string]: number }, withCheckbox: boolean) => {
  const content = document.querySelector('.contentScrollableArea > .content');

  if (content) {
    const computedStyle = getComputedStyle(content);
    const tableWidth =
      content.clientWidth - parseFloat(computedStyle.paddingLeft) - parseFloat(computedStyle.paddingRight);

    const sum = Object.values(colWidth).reduce(
      (acc: number, width: number) => acc + (width ?? 0),
      withCheckbox ? 60 : 0,
    ); // 60 - чекбокс

    if (sum < tableWidth) {
      const count = Object.keys(colWidth).length;
      const addWidth = Math.round((tableWidth - sum) / count);

      const out = { ...colWidth };
      Object.keys(out).forEach((key) => (out[key] = out[key] + addWidth));
      return out;
    }
  }

  return colWidth;
};

let defaultColumnsWidth = {};

// растягивает колонки в справочник, когда открывает таблицу на всю длину экрана
export const useToggleFullSize = ({
  tableName,
  columns,
  fullSize,
  toggle,
  update,
  withCheckbox = true,
}: IUseToggleFullSize): (() => void) => {
  const { setTableContext } = useTableContext();

  const saveTableParams = useCallback(
    (config, widthColumn) => {
      localStorage.setItem(
        TABLE_CONFIG,
        JSON.stringify({
          ...config,
          [tableName]: { ...config[tableName], widthColumn },
        }),
      );

      setTableContext((context) => ({
        ...context,
        state: {
          ...context.state,
          widthColumn,
        },
      }));
    },
    [setTableContext, tableName],
  );

  return useCallback(() => {
    const lsData = localStorage.getItem(TABLE_CONFIG);
    const config = lsData ? JSON.parse(lsData) : {};
    const lsColumnsWidth = config[tableName]?.widthColumn ?? {};

    if (!fullSize) {
      const columnsWidth = {};

      columns.forEach((item) => {
        if (!item.hidden) {
          if (item.key in lsColumnsWidth) {
            columnsWidth[item.key] = lsColumnsWidth[item.key];
          } else columnsWidth[item.key] = item.width;
        }
      });

      saveTableParams(config, calcNewWidth({ ...columnsWidth }, withCheckbox));
      defaultColumnsWidth = { ...columnsWidth };
    } else {
      saveTableParams(config, { ...defaultColumnsWidth });
    }

    toggle();
    update?.();
  }, [columns, fullSize, saveTableParams, tableName, toggle, update, withCheckbox]);
};
